import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { getCategoryById } from '../services/categories';

export const CategoryViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const data = await getCategoryById(id);
        // Handle different response structures
        const categoryData = Array.isArray(data) ? data[0] : (data.category || data);
        setCategory(categoryData);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load category');
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id]);

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading category...</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl">
          <div className="mb-4">
            <Button variant="secondary" onClick={() => navigate('/categories')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Categories
            </Button>
          </div>
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (!category) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl">
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Category not found</p>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl px-4 sm:px-6 lg:px-0">
        <div className="mb-6">
          <Button variant="secondary" onClick={() => navigate('/categories')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Category Details</h1>

        <Card>
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Name</label>
              <p className="text-lg text-gray-900 dark:text-white">{category.name}</p>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Type</label>
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                category.type === 'income' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {category.type.charAt(0).toUpperCase() + category.type.slice(1)}
              </span>
            </div>

            {/* ID */}
            <div className="border-t dark:border-gray-600 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">Category ID</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{category.id}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t dark:border-gray-600">
              <Button 
                variant="primary" 
                onClick={() => navigate(`/categories/${id}/edit`)}
              >
                Edit Category
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/categories')}
              >
                Close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
};
